// MyEngagementsCard — read-only "Mina engagemang" card on the candidate home,
// shown under the profile summary. Lists the candidate's own working
// relationships (active + historical), each with the employer, unit, hired role
// (snapshot) and period. Renders nothing when the candidate has no engagements.
import React, { useEffect, useState } from 'react';
import { Handshake } from 'lucide-react';
import { engagementApi } from '@/lib/api/engagementApi';
import type { CandidateEngagement } from '@/lib/models/engagement';
import { EntityAvatar } from '@/components/custom-ui/EntityAvatar';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('sv-SE') : '');

const MyEngagementsCard: React.FC = () => {
    const [rows, setRows] = useState<CandidateEngagement[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        engagementApi.listMyEngagements()
            .then((r) => { if (!cancelled) setRows(r); })
            .catch(() => { if (!cancelled) setRows([]); });
        return () => { cancelled = true; };
    }, []);

    // Nothing to show (still loading, failed, or genuinely none) → render nothing.
    if (!rows || rows.length === 0) return null;

    return (
        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} mt-6`}>
            <div className="px-5 pt-5 pb-2">
                <h2 className="text-base font-semibold text-gray-900">Mina engagemang</h2>
                <p className="text-sm text-gray-500">Dina anställningar och uppdrag</p>
            </div>
            <div className="px-2 pb-2 divide-y divide-gray-50">
                {rows.map((row) => {
                    const e = row.engagement;
                    const active = !e.ended_at;
                    const period = active
                        ? `Sedan ${fmt(e.started_at)}`
                        : `${fmt(e.started_at)} – ${fmt(e.ended_at)}`;
                    return (
                        <div key={e.id} className="flex items-center gap-3 px-3 py-3">
                            <EntityAvatar icon={Handshake} tone={active ? 'green' : 'neutral'} size="md" orgName={row.organisation_name} orgLogoUrl={row.organisation_logo_url} />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                    {e.role_title || 'Anställd'}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                    {[row.organisation_name, row.unit_name].filter(Boolean).join(' · ')}
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <span
                                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                        active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                    }`}
                                >
                                    {active ? 'Aktiv' : 'Avslutad'}
                                </span>
                                <span className="text-xs text-gray-400">{period}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MyEngagementsCard;
